import React, {useCallback, useEffect, useEffectEvent, useRef, useState} from 'react';
import Modal from 'react-modal';
import {useSelector} from 'react-redux';
import cn from 'classnames';

import {useLanguageDirection, useLockScroll, useMedia, useSetUI, useUI} from 'coreHooks';
import {selectOpenedFlyoutIds} from 'coreStore/data/ui/index';
import {RootState} from 'coreStore/types';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Button from 'brandComponents/Button';
import Icon from 'brandComponents/Icon';
import {FLYOUT_CLASS_NAMES, FLYOUTS_HORIZONTAL_DIRECTION} from 'brandConstants';

import {EFlyoutDirections, IFlyoutProps} from './IFlyout';

//eslint-disable-next-line no-restricted-imports
import './Flyout.scss';

const Flyout = ({
	id,
	children,
	direction = EFlyoutDirections.RIGHT_TO_LEFT,
	onClose,
	className,
	showCloseButton = false,
	closeClassName,
	closeButtonDimension,
	parentSelector = null,
	closeButtonText,
	style,
	softLock,
	closeTimeoutMS: customCloseTimeoutMS,
	closeIconName,
	restoreScrollPosition = true,
	withNavigationDSK,
	withNavigationMOB,
	contentClassName,
	flyoutClassName,
	onAfterClose,
	ref,
	...props
}: IFlyoutProps) => {
	const {isNavigationOpened} = useUI('header');
	const setUI = useSetUI();
	const media = useMedia();
	const isMobile = media('<=small');
	const lockerClassName = softLock || !isMobile;
	const {managerRepository} = useGeneralProvider();
	const openedFlyoutIds = useSelector((state: RootState) => selectOpenedFlyoutIds(state));
	const languageAttributes = useLanguageDirection();
	const [subFlyoutClass, setSubFlyoutClass] = useState('');
	const isFirstRender = useRef(true);
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const onAnimationEndHandlerRef = useRef<() => void | null>(null); // Keep track of the animation end event handler
	const defaultTimeout = !subFlyoutClass ? 850 : 300;
	const closeTimeoutMS = customCloseTimeoutMS ?? defaultTimeout;
	const {lockScroll} = useLockScroll({
		lockerClassName: lockerClassName ? 'soft-lock-scroll' : 'lock-scroll',
		restoreScrollPosition: restoreScrollPosition && !softLock,
		delay: closeTimeoutMS,
	});
	const flyoutIndex = openedFlyoutIds.indexOf(id);
	const isFlyoutOpened = flyoutIndex !== -1;

	const onAfterOpen = useCallback(() => {
		if (!isNavigationOpened) {
			setUI.setNavigationStack({});
		}

		if (props?.onAfterOpen && typeof(props?.onAfterOpen) === 'function') {
			props.onAfterOpen();
		}
	}, [props.onAfterOpen]);

	const shouldUseLeftToRightDirection = () => {
		return (
			languageAttributes.direction === 'rtl' &&
			FLYOUTS_HORIZONTAL_DIRECTION.includes(id) &&
			direction === EFlyoutDirections.RIGHT_TO_LEFT
		);
	};

	if (shouldUseLeftToRightDirection()) {
		direction = EFlyoutDirections.LEFT_TO_RIGHT;
	}

	// Handle the animation end event and clear the subFlyoutClass state
	const onAnimationEnd = useCallback(() => {
		setSubFlyoutClass('');
	}, []);

	const closeFlyout = useCallback(() => {
		managerRepository.getFlyoutsMgr().closeFlyout(id);

		// We need to wait for the animation to finish
		onAnimationEndHandlerRef.current = onAnimationEnd;
	}, [onClose]);

	const handleTransitionEnd = useEffectEvent(() => {
		if (onAnimationEndHandlerRef.current) {
			onAnimationEndHandlerRef.current();
			onAnimationEndHandlerRef.current = null;
		}
	});

	const getClassNames = useCallback(() => {
		const classesClone = {...FLYOUT_CLASS_NAMES.wrapper};

		if (className) {
			classesClone.base += ` ${className}`;
		}

		return classesClone;
	}, [className]);

	// Reads the latest `lockScroll` and `onClose` without running again when they change.
	const onOpenChange = useEffectEvent((isOpened: boolean) => {
		if (isOpened) {
			lockScroll(true);
		} else {
			lockScroll(false);

			if (typeof onClose === 'function' && !isFirstRender.current) {
				onClose();
			}
		}
	});

	useEffect(() => {
		onOpenChange(isFlyoutOpened);
	}, [isFlyoutOpened]);

	// Keep track of sub flyouts
	useEffect(() => {
		if (flyoutIndex > 0) {
			setSubFlyoutClass('flyout--sub-flyout');
		} else if (subFlyoutClass) {
			setSubFlyoutClass('');
		}
	}, [openedFlyoutIds]);

	useEffect(() => {
		isFirstRender.current = false;

		const portalElement = wrapperRef.current;
		const onTransitionEnd = () => handleTransitionEnd();

		if (portalElement) {
			portalElement.addEventListener('transitionend', onTransitionEnd);
		}

		return () => {
			if (portalElement) {
				portalElement.removeEventListener('transitionend', onTransitionEnd);
			}
		};
	}, []);

	const conditionalProps: Record<string, any> = {};

	if (style) {
		conditionalProps.style = style;
	}

	return (
		<Modal
			contentRef={(node) => (wrapperRef.current = node)}
			shouldCloseOnEsc
			isOpen={isFlyoutOpened}
			onRequestClose={closeFlyout}
			shouldCloseOnOverlayClick
			className={getClassNames()}
			bodyOpenClassName={null}
			htmlOpenClassName={null}
			overlayClassName={FLYOUT_CLASS_NAMES.overlay}
			closeTimeoutMS={closeTimeoutMS}
			portalClassName={cn(
				flyoutClassName,
				'flyout',
				`flyout--direction-${direction}`,
				subFlyoutClass,
				{
					'flyout-with-navigation': withNavigationDSK || withNavigationMOB,
					'flyout-with-navigation-desktop': withNavigationDSK,
					'flyout-with-navigation-mobile': withNavigationMOB
				}
			)}
			onAfterClose={onAfterClose}
			onAfterOpen={onAfterOpen}
			parentSelector={parentSelector
				? () => document.querySelector(parentSelector)
				: () => document.getElementById('app-main')
			}
			{...conditionalProps}
		>
			<div className={cn('flyout__content', contentClassName)} ref={ref}>
				{!!showCloseButton && (
					<Button classNames={cn(['flyout__close', closeClassName])} onClick={closeFlyout}>
						{closeButtonText && <span>{closeButtonText}</span>}
						<Icon
							name={closeIconName || 'close'}
							title="Close"
							width={closeButtonDimension?.width || 14}
							height={closeButtonDimension?.height || 14}
						/>
					</Button>
				)}
				{children}
			</div>
		</Modal>
	);
};

export default Flyout;
